import {
  Edit,
  MoreHorizontal,
  Plus,
  Search,
  Trash2,
  UserCheck,
  UserX,
  Shield
} from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Input } from "../components/ui/input";
import { userAPI, permissionAPI, appAPI } from "../utils/api";
import Dialog from "../components/ui/dialog";
import useAuthStore from "../stores/authStore";

const UsersPage = () => {
  const currentUser = useAuthStore((state) => state.user);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({
    full_name: "",
    email: "",
    department: "",
    position: "",
    role: "user",
    is_active: true,
  });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    full_name: "",
    email: "",
    username: "",
    password: "",
    department: "",
    position: "",
    role: "user",
    is_active: true,
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState("");
  const [createFormErrors, setCreateFormErrors] = useState({});
  const [editFormErrors, setEditFormErrors] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  
  // 权限管理相关状态
  const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);
  const [permissionUser, setPermissionUser] = useState(null);
  const [userPermissions, setUserPermissions] = useState([]);
  const [allApps, setAllApps] = useState([]);
  const [permissionLoading, setPermissionLoading] = useState(false);
  const [permissionError, setPermissionError] = useState("");
  const [permissionForm, setPermissionForm] = useState({}); // { appId: permissions }

  const validateCreateForm = (form = createForm) => {
    const errors = {};
    if (!form.username || form.username.length < 3 || form.username.length > 50) {
      errors.username = "ユーザー名は3～50文字で入力してください";
    }
    if (!form.email || !/^[^@]+@[^@]+\.[^@]+$/.test(form.email)) {
      errors.email = "有効なメールアドレスを入力してください";
    }
    if (!form.password || form.password.length < 6) {
      errors.password = "パスワードは6文字以上で入力してください";
    }
    if (!form.full_name) {
      errors.full_name = "氏名を入力してください";
    }
    if (!["admin", "manager", "user"].includes(form.role)) {
      errors.role = "無効なロールです";
    }
    return errors;
  };

  const validateEditForm = (form = editForm) => {
    const errors = {};
    if (!form.username || form.username.length < 3 || form.username.length > 50) {
      errors.username = "ユーザー名は3～50文字で入力してください";
    }
    if (!form.email || !/^[^@]+@[^@]+\.[^@]+$/.test(form.email)) {
      errors.email = "有効なメールアドレスを入力してください";
    }
    if (!form.full_name) {
      errors.full_name = "氏名を入力してください";
    }
    if (!["admin", "manager", "user"].includes(form.role)) {
      errors.role = "無効なロールです";
    }
    return errors;
  };

  // ユーザー一覧を取得
  const fetchUsers = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await userAPI.getUsers({
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm,
        ...params,
      });
      setUsers(response.data.users);
      setPagination(response.data.pagination);
    } catch (err) {
      setError(err.response?.data?.error || "ユーザー一覧の取得に失敗しました");
      console.error("ユーザー一覧取得エラー:", err);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, searchTerm]);

  // 初回ロードと検索
  useEffect(() => {
    fetchUsers();
  }, [searchTerm, fetchUsers]);

  // ユーザー削除
  const handleDeleteUser = (userId) => {
    setDeleteUserId(userId);
    setDeleteError("");
    setDeleteDialogOpen(true);
  };

  // 削除確認
  const handleDeleteConfirm = async () => {
    setDeleteLoading(true);
    setDeleteError("");
    try {
      await userAPI.deleteUser(deleteUserId);
      setDeleteDialogOpen(false);
      fetchUsers();
    } catch (err) {
      setDeleteError(err.response?.data?.error || "ユーザーの削除に失敗しました");
    } finally {
      setDeleteLoading(false);
    }
  };

  // 編集ダイアログを開く
  const handleEditUser = (user) => {
    setEditUser(user);
    setEditForm({
      full_name: user.full_name || "",
      email: user.email || "",
      username: user.username || "",
      department: user.department || "",
      position: user.position || "",
      role: user.role || "user",
      is_active: !!user.is_active,
    });
    setEditError("");
    setEditFormErrors({}); // 前回のエラーをクリア
    setEditDialogOpen(true);
  };

  // 編集フォームの変更
  const handleEditFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // 普通用户不能修改role字段
    if (name === "role" && currentUser?.role !== "admin") {
      return;
    }
    
    const newForm = {
      ...editForm,
      [name]: type === "checkbox" ? checked : value,
    };
    setEditForm(newForm);
    setEditFormErrors(validateEditForm(newForm));
  };

  // 編集を保存
  const handleEditSave = async () => {  
    const errors = validateEditForm();
    setEditFormErrors(errors);
    if (Object.keys(errors).length > 0) return;
    setEditLoading(true);
    setEditError("");
    try {
      // 普通用户不能修改role字段，保持原始值
      const updateData = currentUser?.role === "admin" 
        ? editForm 
        : { ...editForm, role: editUser?.role };
      
      await userAPI.updateUser(editUser.id, updateData);
      setEditDialogOpen(false);
      fetchUsers();
    } catch (err) {
      setEditError(err.response?.data?.error || "ユーザーの更新に失敗しました");
    } finally {
      setEditLoading(false);
    }
  };

  // 新規作成ダイアログを開く
  const handleOpenCreate = () => {
    setCreateForm({
      full_name: "",
      email: "",
      username: "",
      password: "",
      department: "",
      position: "",
      role: "user",
      is_active: true,
    });
    setCreateFormErrors({});
    setCreateDialogOpen(true);
  };

  const handleCreateFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newForm = {
      ...createForm,
      [name]: type === "checkbox" ? checked : value,
    };
    setCreateForm(newForm);
    setCreateFormErrors(validateCreateForm(newForm));
  };

  const handleCreateSave = async () => {
    const errors = validateCreateForm();
    setCreateFormErrors(errors);
    if (Object.keys(errors).length > 0) return;
    setCreateLoading(true);
    setCreateError("");
    try {
      await userAPI.createUser(createForm);
      setCreateDialogOpen(false);
      fetchUsers();
    } catch (err) {
      setCreateError(err.response?.data?.error || "ユーザーの作成に失敗しました");
    } finally {
      setCreateLoading(false);
    }
  };

  // 权限管理对话框を開く
  const handleOpenPermissionDialog = async (user) => {
    setPermissionUser(user);
    setPermissionError("");
    setPermissionDialogOpen(true);
    setPermissionLoading(true);
    
    try {
      // 获取用户权限和应用列表
      const [permissionsRes, appsRes] = await Promise.all([
        permissionAPI.getUserPermissions(user.id),
        appAPI.getApps({ limit: 100 })
      ]);
      
      const permissions = permissionsRes.data.permissions || [];
      const apps = appsRes.data.apps || [];
      
      setUserPermissions(permissions);
      setAllApps(apps);
      
      // 初始化权限表单 - 解析权限值（支持"admin"、"user"或JSON格式），默认为"user"
      const form = {};
      apps.forEach(app => {
        const existingPermission = permissions.find(p => p.app_id === app.id);
        if (existingPermission?.permissions) {
          const permValue = existingPermission.permissions.trim();
          // 如果是简单的"admin"或"user"字符串，直接使用
          if (permValue === "admin" || permValue === "user") {
            form[app.id] = permValue;
          } else {
            // 如果是JSON格式，尝试解析，否则默认为"user"
            try {
              const parsed = JSON.parse(permValue);
              // 如果解析成功，尝试从JSON中提取权限级别
              form[app.id] = parsed.role || parsed.permission || "user";
            } catch {
              // 解析失败，默认为"user"
              form[app.id] = "user";
            }
          }
        } else {
          // 没有权限记录时，默认为"user"
          form[app.id] = "user";
        }
      });
      setPermissionForm(form);
    } catch (err) {
      setPermissionError(err.response?.data?.error || "権限情報の取得に失敗しました");
    } finally {
      setPermissionLoading(false);
    }
  };

  // 权限表单変更
  const handlePermissionFormChange = (appId, value) => {
    setPermissionForm({
      ...permissionForm,
      [appId]: value
    });
  };

  // 权限保存
  const handlePermissionSave = async () => {
    if (!permissionUser) return;
    
    setPermissionLoading(true);
    setPermissionError("");
    
    try {
      // 对每个应用保存权限（默认值为"user"）
      const promises = [];
      
      for (const app of allApps) {
        const permissionValue = permissionForm[app.id] || "user";
        // 始终保存权限值（"admin"或"user"）
        promises.push(
          permissionAPI.grantPermission(permissionUser.id, app.id, permissionValue.trim())
        );
      }
      
      await Promise.all(promises);
      setPermissionDialogOpen(false);
      // 刷新用户列表（可选）
      fetchUsers();
    } catch (err) {
      setPermissionError(err.response?.data?.error || "権限の保存に失敗しました");
    } finally {
      setPermissionLoading(false);
    }
  };


  const filteredUsers = users.filter(
    (user) =>
      user.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRoleBadge = (role) => {
    const roleConfig = {
      admin: { label: "管理者", color: "bg-red-100 text-red-800" },
      manager: {
        label: "マネージャー",
        color: "bg-yellow-100 text-yellow-800",
      },
      user: { label: "ユーザー", color: "bg-blue-100 text-blue-800" },
    };
    const config = roleConfig[role] || roleConfig.user;
    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}
      >
        {config.label}
      </span>
    );
  };

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <UserCheck className="w-3 h-3 mr-1" />
        アクティブ
      </span>
    ) : (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        <UserX className="w-3 h-3 mr-1" />
        非アクティブ
      </span>
    );
  };

  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">ユーザー一覧を読み込み中...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={() => fetchUsers()}>再試行</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ページヘッダー */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">ユーザー管理</h1>
          <p className="text-muted-foreground">
            システムユーザーの管理と権限設定
          </p>
        </div>
        <Button className="flex items-center space-x-2" onClick={handleOpenCreate} disabled={currentUser?.role !== "admin"}>
          <Plus className="h-4 w-4" />
          <span>新規ユーザー</span>
        </Button>
      </div>

      {/* 検索とフィルター */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center space-x-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="ユーザー名、氏名、メールアドレスで検索..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button variant="outline">フィルター</Button>
          </div>
        </CardContent>
      </Card>

      {/* ユーザー一覧 */}
      <Card>
        <CardHeader>
          <CardTitle>ユーザー一覧</CardTitle>
          <CardDescription>
            {pagination.total} 人のユーザーが登録されています
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {filteredUsers.map((user) => (
              <div
                key={user.id}
                className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors"
              >
                <div className="flex items-center space-x-4">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={user.avatar_url} alt={user.full_name} />
                    <AvatarFallback>{user.full_name.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div className="space-y-1">
                    <div className="flex items-center space-x-2">
                      <p className="text-sm font-medium leading-none">
                        {user.full_name}
                      </p>
                      {getRoleBadge(user.role)}
                      {getStatusBadge(user.is_active)}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      @{user.username}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {user.email}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {user.department} • {user.position}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <p className="text-xs text-muted-foreground">
                    最終ログイン:{" "}
                    {user.last_login
                      ? new Date(user.last_login).toLocaleString("ja-JP")
                      : "未ログイン"}
                  </p>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleEditUser(user)} disabled={currentUser?.role !== "admin" && currentUser?.id !== user.id}>
                        <Edit className="mr-2 h-4 w-4" />
                        <span>編集</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => handleOpenPermissionDialog(user)} 
                        disabled={currentUser?.role !== "admin"}
                      >
                        <Shield className="mr-2 h-4 w-4" />
                        <span>権限管理</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={() => handleDeleteUser(user.id)}
                        disabled={currentUser?.role !== "admin"}
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        <span>削除</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))}
          </div>

          {/* ページネーション */}
          {pagination.pages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <p className="text-sm text-muted-foreground">
                {pagination.total} 件中{" "}
                {(pagination.page - 1) * pagination.limit + 1} -{" "}
                {Math.min(pagination.page * pagination.limit, pagination.total)}{" "}
                件を表示
              </p>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page <= 1}
                  onClick={() => fetchUsers({ page: pagination.page - 1 })}
                >
                  前へ
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page >= pagination.pages}
                  onClick={() => fetchUsers({ page: pagination.page + 1 })}
                >
                  次へ
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        title="ユーザー編集"
        footer={
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} disabled={editLoading}>
              キャンセル
            </Button>
            <Button onClick={handleEditSave} disabled={editLoading || Object.keys(editFormErrors).length > 0 || (currentUser?.role !== "admin" && currentUser?.id !== editUser?.id)}>
              {editLoading ? "保存中..." : "保存"}
            </Button>
          </div>
        }
      >
        {editError && <div className="text-red-600 mb-2">{editError}</div>}
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1">氏名</label>
            <Input name="full_name" value={editForm.full_name} onChange={handleEditFormChange} />
            {editFormErrors.full_name && <div className="text-red-600 text-xs">{editFormErrors.full_name}</div>}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">メールアドレス</label>
            <Input name="email" value={editForm.email} onChange={handleEditFormChange} />
            {editFormErrors.email && <div className="text-red-600 text-xs">{editFormErrors.email}</div>}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">ユーザー名</label>
            <Input name="username" value={editForm.username} onChange={handleEditFormChange} />
            {editFormErrors.username && <div className="text-red-600 text-xs">{editFormErrors.username}</div>}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">部署</label>
            <Input name="department" value={editForm.department} onChange={handleEditFormChange} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">役職</label>
            <Input name="position" value={editForm.position} onChange={handleEditFormChange} />
          </div>
          {currentUser?.role === "admin" ? (
            <div>
              <label className="block text-sm font-medium mb-1">ロール</label>
              <select name="role" value={editForm.role} onChange={handleEditFormChange} className="w-full border rounded px-2 py-1">
                <option value="user">ユーザー</option>
                <option value="manager">マネージャー</option>
                <option value="admin">管理者</option>
              </select>
              {editFormErrors.role && <div className="text-red-600 text-xs">{editFormErrors.role}</div>}
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium mb-1">ロール</label>
              <div className="w-full border rounded px-2 py-1 bg-gray-100 text-gray-600">
                {editForm.role === "admin" ? "管理者" : editForm.role === "manager" ? "マネージャー" : "ユーザー"}
              </div>
            </div>
          )}
          <div className="flex items-center space-x-2">
            <input type="checkbox" name="is_active" checked={editForm.is_active} onChange={handleEditFormChange} id="is_active" />
            <label htmlFor="is_active" className="text-sm">アクティブ</label>
          </div>
        </div>
      </Dialog>

      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        title="新規ユーザー作成"
        footer={
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)} disabled={createLoading}>
              キャンセル
            </Button>
            <Button onClick={handleCreateSave} disabled={createLoading || Object.keys(createFormErrors).length > 0 || currentUser?.role !== "admin"}>
              {createLoading ? "作成中..." : "作成"}
            </Button>
          </div>
        }
      >
        {createError && <div className="text-red-600 mb-2">{createError}</div>}
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1">氏名</label>
            <Input name="full_name" value={createForm.full_name} onChange={handleCreateFormChange} />
            {createFormErrors.full_name && <div className="text-red-600 text-xs">{createFormErrors.full_name}</div>}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">メールアドレス</label>
            <Input name="email" value={createForm.email} onChange={handleCreateFormChange} />
            {createFormErrors.email && <div className="text-red-600 text-xs">{createFormErrors.email}</div>}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">ユーザー名</label>
            <Input name="username" value={createForm.username} onChange={handleCreateFormChange} />
            {createFormErrors.username && <div className="text-red-600 text-xs">{createFormErrors.username}</div>}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">パスワード</label>
            <Input name="password" type="password" value={createForm.password} onChange={handleCreateFormChange} />
            {createFormErrors.password && <div className="text-red-600 text-xs">{createFormErrors.password}</div>}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">部署</label>
            <Input name="department" value={createForm.department} onChange={handleCreateFormChange} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">役職</label>
            <Input name="position" value={createForm.position} onChange={handleCreateFormChange} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">ロール</label>
            <select name="role" value={createForm.role} onChange={handleCreateFormChange} className="w-full border rounded px-2 py-1">
              <option value="user">ユーザー</option>
              <option value="manager">マネージャー</option>
              <option value="admin">管理者</option>
            </select>
            {createFormErrors.role && <div className="text-red-600 text-xs">{createFormErrors.role}</div>}
          </div>
          <div className="flex items-center space-x-2">
            <input type="checkbox" name="is_active" checked={createForm.is_active} onChange={handleCreateFormChange} id="create_is_active" />
            <label htmlFor="create_is_active" className="text-sm">アクティブ</label>
          </div>
        </div>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title="ユーザー削除の確認"
        footer={
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
              キャンセル
            </Button>
            <Button onClick={handleDeleteConfirm} disabled={deleteLoading} variant="destructive">
              {deleteLoading ? "削除中..." : "削除"}
            </Button>
          </div>
        }
      >
        {deleteError && <div className="text-red-600 mb-2">{deleteError}</div>}
        <div>本当にこのユーザーを削除しますか？この操作は取り消せません。</div>
      </Dialog>

      {/* 权限管理对话框 */}
      <Dialog
        open={permissionDialogOpen}
        onClose={() => setPermissionDialogOpen(false)}
        title={`アプリケーション権限管理 - ${permissionUser?.full_name || ""}`}
        footer={
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setPermissionDialogOpen(false)} disabled={permissionLoading}>
              キャンセル
            </Button>
            <Button onClick={handlePermissionSave} disabled={permissionLoading || currentUser?.role !== "admin"}>
              {permissionLoading ? "保存中..." : "保存"}
            </Button>
          </div>
        }
      >
        {permissionError && <div className="text-red-600 mb-4">{permissionError}</div>}
        
        {permissionLoading && allApps.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">権限情報を読み込み中...</p>
            </div>
          </div>
        ) : (
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div className="text-sm text-muted-foreground mb-4">
              各アプリケーションに対する権限レベルを設定できます。
            </div>
            
            {allApps.map((app) => {
              const existingPermission = userPermissions.find(p => p.app_id === app.id);
              const hasPermission = !!existingPermission;
              const permissionValue = permissionForm[app.id] || "user";
              
              // 获取权限徽章显示
              const getPermissionBadge = (value) => {
                if (value === "admin") {
                  return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">管理者</span>;
                }
                if (value === "user") {
                  return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">ユーザー</span>;
                }
                return null;
              };
              
              return (
                <Card key={app.id} className={hasPermission ? "border-primary" : ""}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-base">{app.display_name}</CardTitle>
                        <CardDescription className="text-xs">{app.description || app.name}</CardDescription>
                      </div>
                      {getPermissionBadge(permissionValue)}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        権限レベル
                      </label>
                      <div className="space-y-2">
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="radio"
                            name={`permission-${app.id}`}
                            value="user"
                            checked={permissionValue === "user"}
                            onChange={(e) => handlePermissionFormChange(app.id, e.target.value)}
                            disabled={currentUser?.role !== "admin"}
                            className="w-4 h-4"
                          />
                          <span className="text-sm">ユーザー権限</span>
                        </label>
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="radio"
                            name={`permission-${app.id}`}
                            value="admin"
                            checked={permissionValue === "admin"}
                            onChange={(e) => handlePermissionFormChange(app.id, e.target.value)}
                            disabled={currentUser?.role !== "admin"}
                            className="w-4 h-4"
                          />
                          <span className="text-sm">管理者権限</span>
                        </label>
                      </div>
                    </div>
                    {hasPermission && (
                      <div className="pt-2 border-t">
                        <span className="text-xs text-muted-foreground">
                          付与日時: {existingPermission.granted_at 
                            ? new Date(existingPermission.granted_at).toLocaleString("ja-JP")
                            : "不明"}
                        </span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
            
            {allApps.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                アプリケーションが見つかりません
              </div>
            )}
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default UsersPage;
